import React, { useState, useEffect } from 'react';

import { Button, Modal } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { decode } from 'html-entities';

import { useAppSelector } from 'hooks';
import { Dropdown } from 'components/Forms';
import { useAxios } from 'configs/requests';
import { Error, Loading } from 'components/Fetch';

interface IAddSmartEventsModalProps {
  setShow: (show: boolean) => any;
  show: boolean;
  getCarePlan: () => any;
  currentSmartEvents: string[];
  carePlanTemplate?: ICarePlanTemplate;
}

const AddSmartEventsModal = ({
  show,
  setShow,
  getCarePlan,
  currentSmartEvents,
  carePlanTemplate
}: IAddSmartEventsModalProps) => {
  const intl = useIntl();
  const patientUid = useAppSelector(({ patient }) => patient.data?.uid);
  const selectedGid = useAppSelector(({ groups }) => groups?.selectedGid);
  const patientGid = useAppSelector(({ patient }) => patient.data?.subgroup_gid);
  const [smartEventsSelected, setSmartEventsSelected] = useState<ISelectOption[]>([]);
  const [error, setError] = useState<any>(false);

  const [{ data: getSmartEventsData }] = useAxios(
    {
      url: `/clinician/SmartEventsCatalog?group_id=${patientGid || selectedGid}`,
      method: 'GET'
    },
    { manual: false, useCache: false }
  );
  const smartEventsData: IGetSmartEvent[] = getSmartEventsData?.events || [];
  const smartEventsOptions: ISelectOption[] = smartEventsData
    .filter((event) => !currentSmartEvents.includes(event.attribute))
    .sort((a, b) => {
      const labelA = intl
        .formatMessage({ id: decode(a.summary) })
        .toLowerCase()
        .trim();
      const labelB = intl
        .formatMessage({ id: decode(b.summary) })
        .toLowerCase()
        .trim();
      return labelA.localeCompare(labelB);
    })
    .map((event) => {
      return {
        value: event.attribute,
        label: intl.formatMessage({ id: decode(event.summary) })
      };
    });

  const [{ loading, error: postError }, post] = useAxios(
    {
      url: carePlanTemplate
        ? '/clinician/2.0.0/CarePlanTemplate'
        : `/clinician/Patient/${patientUid}/CarePlan`,
      method: 'POST'
    },
    { manual: true }
  );

  const handleAddSmartEvent = async () => {
    if (!smartEventsSelected.length) return;

    const newData = smartEventsSelected.map((event) => ({
      attribute: event.value,
      edit_status: 'new',
      attribute_type: 'carePlanSmartAlert',
      value: 1
    }));
    await post({
      data: {
        smartEvents: [...newData],
        carePlanTemplate
      }
    });
    setSmartEventsSelected([]);
    setShow(false);
    getCarePlan();
  };

  const handleOnChange = (options: ISelectOption[]) => {
    setError(false);
    setSmartEventsSelected(options);
  };

  const handleClose = () => {
    setSmartEventsSelected([]);
    setError(false);
    setShow(false);
  };

  useEffect(() => {
    setError(postError);
  }, [postError, setError]);

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop="static"
      data-test="careplan-smart-events-modal"
    >
      <Modal.Header className="align-items-center">
        <Modal.Title>
          {intl.formatMessage({ id: 'add' }) + ' ' + intl.formatMessage({ id: 'smartEventsKey' })}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Error retry={handleAddSmartEvent} dismissible onClose={() => setError(false)} />}
        <Dropdown
          dataTestId="add-smart-event-select"
          variant="form"
          options={smartEventsOptions}
          onChange={(options: ISelectOption[]) => handleOnChange(options || [])}
          isMulti
          value={smartEventsSelected}
          isClearable={false}
          wrapLines={true}
        />
        {loading && <Loading className="mt-4" />}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="link"
          onClick={handleClose}
          data-test="careplan-smart-events-modal-close-btn"
        >
          {intl.formatMessage({ id: 'closeKey' })}
        </Button>
        <Button
          variant="primary"
          onClick={handleAddSmartEvent}
          disabled={!smartEventsSelected.length}
          data-test="careplan-smart-events-modal-submit-btn"
        >
          {intl.formatMessage({ id: 'add' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddSmartEventsModal;
